import type { CommunityEntry } from "@cfg/api-contract";
import type { PrintTips } from "@cfg/design-sdk";
import { useEffect, useState } from "react";
import { useApp } from "../app-context.tsx";
import { useAuthUser } from "../auth.ts";
import type { DownloadRecord } from "../download-log.ts";
import { useFitInfo } from "../fits.ts";
import { useCommunityCache, useMyCommunity } from "./CommunityPanel.tsx";

/** PLA density, g/cm³: the filament estimate is for a solid part, an upper bound. */
const PLA_G_PER_CM3 = 1.24;

/** "≤ 3.5 g": grams of PLA for a solid part of `volumeCm3`, one decimal under 10 g. */
export function filamentGrams(volumeCm3: number): string {
  const g = volumeCm3 * PLA_G_PER_CM3;
  return g < 10 ? g.toFixed(1) : String(Math.round(g));
}

export interface AfterDownloadProps {
  /** The file just saved; absent when the card opened from the "did it fit?" reminder. */
  file?: { filename: string; size: string };
  designId: string;
  /** Solid volume of the part on the model, cm³. */
  volumeCm3?: number;
  /** `meta.print` and `meta.printNotes` of the design. */
  print?: PrintTips;
  printNotes?: string;
  /** The community bracket on the model, if the download was one. */
  entry?: CommunityEntry;
  /** Set the visitor came for (`?fit=`). */
  fit?: string;
  /** The params on the model are saved (profile or device). */
  saved: boolean;
  /** Show the Save button of the My bracket block. */
  onSave: () => void;
  /** Open the tag step with "I printed it" ticked, and the set when known. */
  onTag: (fit?: { ref: string; name?: string }) => void;
  /** The visitor said they printed it: don't ask again. */
  onPrinted: () => void;
  onClose: () => void;
}

/**
 * "Next steps" after a download: how to print it (the design's tips, a
 * filament estimate), then "Printed it?", which registers the print on the
 * community bracket that was downloaded or opens the tag step for your own
 * dimensions, plus a nudge to save unsaved ones. Stays until closed or the
 * dimensions change.
 */
export function AfterDownload({ file, designId, volumeCm3, print, printNotes, entry, fit, saved, onSave, onTag, onPrinted, onClose }: AfterDownloadProps) {
  const { api, auth, brand, copy } = useApp();
  const user = useAuthUser(auth);
  const mine = useMyCommunity();
  const cache = useCommunityCache(designId);
  const refs = entry?.fits.map((f) => f.ref) ?? [];
  const preselect = () => (fit && (!entry || refs.includes(fit)) ? fit : refs.length === 1 ? refs[0] : "");
  const [ref, setRef] = useState(preselect);
  // The entry can arrive after the card (the community list loads later): preselect then.
  useEffect(() => {
    if (entry && !ref) setRef(preselect());
  }, [entry?.id]);
  const info = useFitInfo(ref || undefined, entry?.fits.find((f) => f.ref === ref)?.name);
  const [state, setState] = useState<{ busy: boolean; error?: string }>({ busy: false });
  /** The save nudge was followed (the URL only says "saved" after the next render of the page). */
  const [toSave, setToSave] = useState(false);
  const printed = !!(entry && ref && mine.data?.prints.includes(`${entry.id}#${ref}`));
  const gallery = brand.config.features.publicGallery && brand.config.features.fitTags && !!api;

  const tips: [string, string][] = [];
  if (print?.orientation) tips.push([copy("next.tip.orientation"), print.orientation]);
  if (print?.supports !== undefined) tips.push([copy("next.tip.supports"), copy(print.supports ? "next.tip.supportsYes" : "next.tip.supportsNo")]);
  if (print?.layerHeight) tips.push([copy("next.tip.layerHeight"), `${print.layerHeight} mm`]);
  if (print?.infill !== undefined) tips.push([copy("next.tip.infill"), `${print.infill} %`]);
  if (print?.walls) tips.push([copy("next.tip.walls"), String(print.walls)]);
  if (print?.material) tips.push([copy("next.tip.material"), print.material]);
  if (print?.hardware) tips.push([copy("next.tip.hardware"), print.hardware]);

  const registerPrint = async () => {
    if (!entry || !ref || !api) return;
    if (!user) return void auth.signIn();
    setState({ busy: true });
    try {
      const r = await api.confirmPublic(entry.id, ref);
      cache.upsert(r.entry, { front: false });
      cache.markMine(entry.id, [], [ref]);
      setState({ busy: false });
      onPrinted();
    } catch (err) {
      setState({ busy: false, error: (err as Error).message });
    }
  };

  return (
    <section className="after-download" aria-labelledby="after-download-title" aria-live="polite">
      <header className="after-download-head">
        <h3 id="after-download-title">{file ? copy("next.downloaded", { filename: file.filename, size: file.size }) : copy("next.title")}</h3>
        <button type="button" className="cfg-live-close after-download-close" aria-label={copy("common.dismiss")} onClick={onClose}>
          ×
        </button>
      </header>

      <div className="after-download-block">
        <h4>{copy("next.printTitle")}</h4>
        {tips.length > 0 && (
          <dl className="after-download-tips">
            {tips.map(([k, v]) => (
              <div key={k}>
                <dt>{k}</dt>
                <dd>{v}</dd>
              </div>
            ))}
          </dl>
        )}
        {printNotes && <p className="after-download-notes">{printNotes}</p>}
        {!!volumeCm3 && <p className="muted small">{copy("next.filament", { grams: filamentGrams(volumeCm3), volume: volumeCm3.toFixed(1) })}</p>}
      </div>

      {gallery && (
        <div className="after-download-block">
          <h4>{copy("next.printedTitle")}</h4>
          <p className="muted small">{copy("next.printedHint")}</p>
          {entry ? (
            printed ? (
              <p className="after-download-done" role="status">
                {copy("next.printedDone", { ref })}
              </p>
            ) : (
              <div className="after-download-row">
                {refs.length > 1 && (
                  <select className="cfg-input" aria-label={copy("next.printedPick")} value={ref} onChange={(e) => setRef(e.target.value)}>
                    {!ref && <option value="">{copy("next.printedPick")}</option>}
                    {entry.fits.map((f) => (
                      <option key={f.ref} value={f.ref}>
                        {f.ref}
                        {f.name ? ` ${f.name}` : ""}
                      </option>
                    ))}
                  </select>
                )}
                <button type="button" className="cfg-btn cfg-btn-primary" disabled={!ref || state.busy} onClick={() => void registerPrint()}>
                  {!user ? copy("next.signIn") : ref ? copy("next.printedFor", { ref: info?.name ? `${ref} ${info.name}` : ref }) : copy("next.printedPick")}
                </button>
              </div>
            )
          ) : (
            <div className="after-download-row">
              <button type="button" className="cfg-btn cfg-btn-primary" onClick={() => onTag(fit ? { ref: fit, name: info?.name } : undefined)}>
                {!user ? copy("next.signIn") : fit ? copy("next.printedFor", { ref: info?.name ? `${fit} ${info.name}` : fit }) : copy("next.printedTag")}
              </button>
            </div>
          )}
          {state.error && <p className="cfg-error">{state.error}</p>}
        </div>
      )}

      {!entry && !saved && !toSave && (
        <p className="after-download-save">
          <button
            type="button"
            className="link"
            onClick={() => {
              setToSave(true);
              onSave();
            }}
          >
            {copy("next.save")}
          </button>
        </p>
      )}
    </section>
  );
}

/** "You downloaded … on Oct 18. Did it fit?": once per download, on a later visit to the design (or its family). */
export function DownloadReminder({ record, onYes, onNo, onDismiss }: { record: DownloadRecord; onYes: () => void; onNo: () => void; onDismiss: () => void }) {
  const { copy, locale } = useApp();
  const date = new Date(record.at).toLocaleDateString(locale, { month: "short", day: "numeric" });
  return (
    <div className="download-reminder" role="status">
      <p>
        {copy("next.reminder", { name: record.name, date })}
        {record.fit && <span className="download-reminder-fit"> · {record.fit}</span>}
      </p>
      <div className="download-reminder-actions">
        <button type="button" className="cfg-btn cfg-btn-primary" onClick={onYes}>
          {copy("next.reminderYes")}
        </button>
        <button type="button" className="cfg-btn" onClick={onNo}>
          {copy("next.reminderNo")}
        </button>
        <button type="button" className="cfg-live-close" aria-label={copy("common.dismiss")} onClick={onDismiss}>
          ×
        </button>
      </div>
    </div>
  );
}
